import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import {
  ScanLine, Search, LogOut, ChevronDown, Loader2, CalendarDays, Users, Wifi, WifiOff, Volume2, VolumeX,
} from "lucide-react";
import { toast } from "sonner";
import {
  staffApi, getStaffToken, getStaffCtx, getStaffEvent, setStaffEvent, clearStaffSession,
} from "../../lib/staff";
import { flushQueue, queueSize, onQueueChange } from "../../lib/checkinQueue";
import { isSoundEnabled, setSoundEnabled, resumeAudio } from "../../lib/sound";
import InstallPrompt from "../../components/InstallPrompt";

const DATE_KEY = "st_staff_date";

function todayInTz(tz) {
  try { return new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(new Date()); }
  catch { return new Date().toISOString().slice(0, 10); }
}
function fmtDate(ds) {
  if (!ds) return "";
  try {
    return new Date(ds + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  } catch { return ds; }
}

export default function StaffApp() {
  const nav = useNavigate();
  const hasToken = !!getStaffToken();
  const [ctx, setCtx] = useState(getStaffCtx());
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(getStaffEvent());
  const [date, setDate] = useState(() => { try { return localStorage.getItem(DATE_KEY) || ""; } catch { return ""; } });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [evtPickerOpen, setEvtPickerOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(queueSize());
  const [soundOn, setSoundOn] = useState(isSoundEnabled());

  const tz = ctx?.attraction?.timezone || "Europe/London";

  // ---- available dates for the counter (selected event, or union across events) ----
  const availableDates = useMemo(() => {
    let ds = [];
    if (eventId) {
      const e = events.find((x) => x.id === eventId);
      ds = (e?.dates || []).map((d) => d.date);
    } else {
      ds = events.flatMap((e) => (e.dates || []).map((d) => d.date));
    }
    return Array.from(new Set(ds.filter(Boolean))).sort();
  }, [events, eventId]);

  // pick a sensible default date whenever the list changes
  useEffect(() => {
    if (!availableDates.length) { setDate(""); return; }
    if (date && availableDates.includes(date)) return;
    const today = todayInTz(tz);
    const next = availableDates.includes(today)
      ? today
      : (availableDates.find((d) => d >= today) || availableDates[availableDates.length - 1]);
    setDate(next);
  }, [availableDates, tz]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { try { if (date) localStorage.setItem(DATE_KEY, date); } catch { /* noop */ } }, [date]);

  // ---- load context ----
  useEffect(() => {
    if (!hasToken) return;
    let active = true;
    (async () => {
      try {
        const { data } = await staffApi.get("/staff/context");
        if (!active) return;
        setCtx({ staff: data.staff, attraction: data.attraction });
        setEvents(data.events || []);
      } catch (err) {
        if (err?.response?.status === 401) { clearStaffSession(); nav("/app/login", { replace: true }); }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [hasToken, nav]);

  // ---- live night counter ----
  const refreshSummary = useCallback(async () => {
    if (!date) { setSummary(null); return; }
    try {
      const { data } = await staffApi.get("/staff/summary", { params: { date, event_id: eventId || undefined } });
      setSummary(data);
    } catch { /* keep last */ }
  }, [date, eventId]);

  useEffect(() => { refreshSummary(); }, [refreshSummary]);
  useEffect(() => {
    const t = setInterval(refreshSummary, 20000);
    return () => clearInterval(t);
  }, [refreshSummary]);

  // ---- offline queue + reconnect ----
  const flushingRef = useRef(false);
  const doFlush = useCallback(async () => {
    if (flushingRef.current || !navigator.onLine || queueSize() === 0) return;
    flushingRef.current = true;
    try {
      const res = await flushQueue((item) =>
        staffApi.post("/checkin", { attendee_id: item.attendee_id, device: item.device, force: true }).then((r) => r.data));
      if (res.processed > 0) { toast.success(`Synced ${res.processed} offline check-in${res.processed > 1 ? "s" : ""}`); refreshSummary(); }
    } finally {
      flushingRef.current = false;
    }
  }, [refreshSummary]);

  useEffect(() => {
    const onOn = () => { setOnline(true); doFlush(); };
    const onOff = () => setOnline(false);
    window.addEventListener("online", onOn);
    window.addEventListener("offline", onOff);
    const off = onQueueChange(() => setPending(queueSize()));
    const t = setInterval(doFlush, 15000);
    doFlush();
    return () => {
      window.removeEventListener("online", onOn);
      window.removeEventListener("offline", onOff);
      off();
      clearInterval(t);
    };
  }, [doFlush]);

  // Resume the audio context on the first user gesture (mobile autoplay policy).
  useEffect(() => {
    const on = () => resumeAudio();
    window.addEventListener("pointerdown", on, { once: true });
    return () => window.removeEventListener("pointerdown", on);
  }, []);

  const logout = async () => {
    try { await staffApi.post("/staff/logout"); } catch { /* noop */ }
    clearStaffSession();
    toast.success("Signed out");
    nav("/app/login", { replace: true });
  };

  const pickEvent = (id) => { setEventId(id); setStaffEvent(id); setEvtPickerOpen(false); };
  const pickDate = (d) => { setDate(d); setDatePickerOpen(false); };

  const currentEvent = useMemo(() => events.find((e) => e.id === eventId), [events, eventId]);

  if (!hasToken) return <Navigate to="/app/login" replace />;

  const tabClass = (active) =>
    `flex flex-col items-center justify-center gap-1 flex-1 py-2.5 text-xs font-semibold transition-colors ${
      active ? "text-[#dc2626]" : "text-[#8a8a8a]"}`;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col" data-testid="staff-app">
      <InstallPrompt />
      <header
        className="sticky top-0 z-40 bg-[#0c0c0c]/95 backdrop-blur border-b border-[#1a1a1a] px-4 py-2.5"
        style={{ paddingTop: "max(0.625rem, env(safe-area-inset-top))" }}
        data-testid="staff-header"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="st-brand text-base leading-tight">SCARE<span className="text-[#dc2626]">TICKETS</span></div>
            <div className="text-[11px] text-[#8a8a8a] truncate" data-testid="staff-venue-name">
              {ctx?.attraction?.name} · {ctx?.staff?.device}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span
              className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border ${
                online ? "text-[#22c55e] border-[#22c55e]/30 bg-[#14532d]/20" : "text-[#f59e0b] border-[#f59e0b]/30 bg-[#78350f]/20"}`}
              data-testid="staff-online-status"
            >
              {online ? <Wifi size={12} /> : <WifiOff size={12} />}
              {online ? "Online" : "Offline"}{pending > 0 ? ` · ${pending}` : ""}
            </span>
            <button
              onClick={() => { const v = !soundOn; setSoundOn(v); setSoundEnabled(v); }}
              className="st-btn st-btn-ghost !px-2 !py-1.5 text-xs"
              title={soundOn ? "Mute scan sounds" : "Unmute scan sounds"}
              data-testid="staff-sound-toggle"
            >
              {soundOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
            </button>
            <button onClick={logout} className="st-btn st-btn-ghost !px-2 !py-1.5 text-xs" data-testid="staff-logout">
              <LogOut size={14} />
            </button>
          </div>
        </div>

        {/* Event selector */}
        <div className="relative mt-2">
          <button
            onClick={() => { setEvtPickerOpen((o) => !o); setDatePickerOpen(false); }}
            className="w-full flex items-center justify-between gap-2 rounded-lg border border-[#1a1a1a] bg-[#0e0e0e] px-3 py-2 text-sm"
            data-testid="staff-event-selector"
          >
            <span className="flex items-center gap-2 min-w-0">
              <ScanLine size={15} className="text-[#dc2626] shrink-0" />
              <span className="truncate">{currentEvent ? currentEvent.name : "All events"}</span>
            </span>
            <ChevronDown size={16} className={`text-[#8a8a8a] transition-transform ${evtPickerOpen ? "rotate-180" : ""}`} />
          </button>
          {evtPickerOpen && (
            <div className="absolute left-0 right-0 mt-1 z-50 rounded-lg border border-[#1a1a1a] bg-[#101010] shadow-xl overflow-hidden" data-testid="staff-event-list">
              <button onClick={() => pickEvent("")} className={`w-full text-left px-3 py-2.5 text-sm ${!eventId ? "text-[#dc2626]" : "text-[#cfcfcf]"} hover:bg-[#161616]`} data-testid="staff-event-all">All events</button>
              {events.map((e) => (
                <button key={e.id} onClick={() => pickEvent(e.id)} className={`w-full text-left px-3 py-2.5 text-sm border-t border-[#161616] ${eventId === e.id ? "text-[#dc2626]" : "text-[#cfcfcf]"} hover:bg-[#161616]`} data-testid={`staff-event-${e.id}`}>{e.name}</button>
              ))}
            </div>
          )}
        </div>

        {/* Date selector + live night counter */}
        {availableDates.length > 0 && (
          <div className="flex items-stretch gap-2 mt-2">
            <div className="relative flex-1">
              <button
                onClick={() => { setDatePickerOpen((o) => !o); setEvtPickerOpen(false); }}
                className="w-full h-full flex items-center justify-between gap-2 rounded-lg border border-[#1a1a1a] bg-[#0e0e0e] px-3 py-2 text-sm"
                data-testid="staff-date-selector"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <CalendarDays size={15} className="text-[#dc2626] shrink-0" />
                  <span className="truncate">{fmtDate(date)}</span>
                </span>
                <ChevronDown size={16} className={`text-[#8a8a8a] transition-transform ${datePickerOpen ? "rotate-180" : ""}`} />
              </button>
              {datePickerOpen && (
                <div className="absolute left-0 right-0 mt-1 z-50 max-h-60 overflow-y-auto rounded-lg border border-[#1a1a1a] bg-[#101010] shadow-xl" data-testid="staff-date-list">
                  {availableDates.map((d) => (
                    <button key={d} onClick={() => pickDate(d)} className={`w-full text-left px-3 py-2.5 text-sm border-b border-[#161616] last:border-0 ${date === d ? "text-[#dc2626]" : "text-[#cfcfcf]"} hover:bg-[#161616]`} data-testid={`staff-date-${d}`}>{fmtDate(d)}</button>
                  ))}
                </div>
              )}
            </div>
            <div
              className="flex items-center gap-1.5 rounded-lg border border-[#22c55e]/30 bg-[#14532d]/20 px-3 text-sm font-semibold shrink-0"
              data-testid="staff-night-counter"
            >
              <Users size={15} className="text-[#22c55e]" />
              <span className="text-[#22c55e]">{summary?.checked_in ?? 0}</span>
              <span className="text-[#8a8a8a]">{"\u00A0/\u00A0"}{summary?.total ?? 0} in</span>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 flex flex-col min-h-0">
        {loading ? (
          <div className="flex-1 flex items-center justify-center text-[#8a8a8a]"><Loader2 className="animate-spin" size={22} /></div>
        ) : (
          <Outlet context={{ eventId, date, events, attraction: ctx?.attraction, staff: ctx?.staff, online, refreshSummary }} />
        )}
      </main>

      <nav
        className="sticky bottom-0 z-40 bg-[#0c0c0c]/95 backdrop-blur border-t border-[#1a1a1a] flex items-stretch"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="staff-bottom-nav"
      >
        <NavLink to="/app" end className={({ isActive }) => tabClass(isActive)} data-testid="staff-tab-scan"><ScanLine size={22} /> Scan</NavLink>
        <NavLink to="/app/search" className={({ isActive }) => tabClass(isActive)} data-testid="staff-tab-search"><Search size={22} /> Search</NavLink>
      </nav>
    </div>
  );
}
